import { useMemo, useRef } from 'react'
import { AuthError, AuthErrorCode } from '../../errors'
import { apiFetch } from '../../helpers/apiFetch'
import { missingRefreshToken } from '../../helpers/missingRefreshToken'
import { resolveTraceHeaders } from '../../helpers/resolveTraceHeaders'
import { isTokenExpired } from '../../helpers/isExpired'
import type { AuthConfig } from '../../types'

const REFRESH_ACCESS_TOKEN_MUTATION = `mutation RefreshAccessToken($data: AuthMutationsRefreshInput) {
  auth {
    refresh(data: $data) {
      refreshToken
      accessToken
    }
  }
}`

type AuthErrorProps = Parameters<typeof AuthError.create>[0]

const API_ERRORS_TO_AUTH_ERROR_PROPS: Record<string, AuthErrorProps> = {
  UNAUTHENTICATED: {
    code: AuthErrorCode.refreshTokenInvalid,
    message: 'user is not authenticated',
  },
  FORBIDDEN: {
    code: AuthErrorCode.notAuthorized,
    message: 'user is not authorized',
  },
}

export const useGenerateFetchAccessTokenAction = (config: AuthConfig) => {
  const {
    accessTokenRef,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    onError,
    apiUrl,
    getTraceHeaders,
  } = config
  const activePromise = useRef<Promise<string | null> | undefined>()

  return useMemo(() => {
    const executeRefresh = async () => {
      // Logged out: fail at once, before the trace getter does any device I/O
      if (!getRefreshToken()) throw missingRefreshToken()
      // Resolve the headers first: the token is read right before it is sent
      // so a rotation or logout during the await is not undone
      const headers = await resolveTraceHeaders(getTraceHeaders)
      const refreshToken = getRefreshToken()
      if (!refreshToken) throw missingRefreshToken()

      const response = await apiFetch(
        apiUrl,
        {
          operationName: 'RefreshAccessToken',
          variables: {
            data: {
              refreshToken,
            },
          },
          query: REFRESH_ACCESS_TOKEN_MUTATION,
        },
        headers,
      )

      const { errors } = response
      if (errors?.length) {
        if (isTokenExpired(refreshToken)) {
          setRefreshToken(null)
          setAccessToken(null)
          throw AuthError.create({
            code: AuthErrorCode.refreshTokenExpired,
            message: 'refresh token has expired',
          })
        }

        for (const err of errors) {
          const apiCode = err.extensions?.code
          const authError = apiCode
            ? API_ERRORS_TO_AUTH_ERROR_PROPS[apiCode]
            : undefined
          if (authError) {
            setRefreshToken(null)
            setAccessToken(null)
            throw AuthError.create(authError)
          }
        }

        throw AuthError.create({
          code: AuthErrorCode.refreshFailed,
          message: errors.map((err) => err.message).join('\n'),
        })
      }

      const tokens = response.data?.auth?.refresh
      if (!tokens) {
        throw AuthError.create({
          code: AuthErrorCode.refreshTokenNotFound,
          message: 'missing refresh token in response',
        })
      }

      setRefreshToken(tokens.refreshToken || null)
      setAccessToken(tokens.accessToken || null)
      return tokens.accessToken ?? null
    }

    const fetchAccessToken = (force?: boolean): Promise<string | null> => {
      const currentToken = accessTokenRef.current
      const shouldRefresh = Boolean(
        !currentToken ||
          (currentToken?.refetchAt && currentToken.refetchAt < Date.now()),
      )
      if (!shouldRefresh && !force) {
        return Promise.resolve(currentToken?.token || null)
      }

      activePromise.current ||= (async () => {
        let newToken: string | null = null
        try {
          newToken = await executeRefresh()
        } catch (err) {
          onError?.(err as AuthError)
        }
        activePromise.current = undefined
        return newToken
      })()

      return activePromise.current
    }

    return fetchAccessToken
  }, [
    apiUrl,
    accessTokenRef,
    getRefreshToken,
    setRefreshToken,
    setAccessToken,
    onError,
    getTraceHeaders,
  ])
}
